import * as d3 from "d3";
import { useEffect, useRef, useState, useContext } from "react";
import { Modal, Steps, Button, Collapse, Timeline, Tag, Card } from "antd";
import {
  EnvironmentOutlined,
  ClockCircleFilled,
  LeftOutlined,
  RightOutlined,
  CalendarOutlined,
  FileTextOutlined
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { AuthContext } from "../../context/AuthContext";
import SampleImg from "../../assests/sample.jpg";

const { Step } = Steps;
const { Panel } = Collapse;

export default function CircularOverAll({ onBack }) {
  const ref = useRef();
  const [selectedDate, setSelectedDate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month());
  const { authData, backUrl } = useContext(AuthContext);
  const [overAllMonthData, setOverAllMonthData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const [loading, setLoading] = useState(false);

  const [showOnDuty, setShowOnDuty] = useState(false);
  const [onDutyRequests, setOnDutyRequests] = useState([]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${backUrl}/api/hr/attendance-comparsion/`, {
        headers: { Authorization: `Bearer ${authData?.access}` },
        params: { month: month + 1, year },
      });
      setOverAllMonthData(res.data.results || []);
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      setOverAllMonthData([]);
    }
  };

  const fetchOnduty = async (date) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${backUrl}/api/hr/attendance/my-onduty-requests/?from_date=${date}&to_date=${date}`,
        {
          headers: {
            Authorization: `Bearer ${authData?.access}`,
          },
        }
      );
      setOnDutyRequests(response.data);
    } catch (err) {
      console.log(err.response?.data?.message || err.message);
      console.log("Failed to fetch onduty requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [month, year]);

  const filteredData = overAllMonthData.filter(
    (item) => item.Date === selectedDate
  );

  // Filter to only show records with FirstInTime
  const validFilteredData = filteredData.filter(
    (item) => item?.FirstInTime !== null && item?.FirstInTime !== undefined
  );

  const getStatusColor = (status) => {
    switch (status) {
      case "APPROVED":
        return "#16a34a";
      case "PENDING":
        return "#d97706";
      case "REJECTED":
        return "#dc2626";
      default:
        return "#888";
    }
  };

  const getDayColor = (dateStr) => {
    const dayRecords = overAllMonthData.filter((item) => item.Date === dateStr);
    if (!dayRecords.length) return "#d1d5db";
    let color = "#16a34a";
    for (const rec of dayRecords) {
      if (rec.Status === "PRESENT") { color = "#16a34a"; break; }
      if (rec.Status === "ABSENT") { color = "#dc2626"; break; }
      if (rec.Status === "CASUAL LEAVE") { color = "#CB00CC"; break; }
      if (rec.Status === "SICK LEAVE") { color = "#FF0066"; break; }
      if (rec.Status === "WEEK OFF") { color = "yellow"; break; }
      if (rec.Status === "LEAVE") { color = "#dc2626"; break; }
      if (rec.Status === "COMPENSATORY OFF") { color = "#1FA2CC"; break; }
      if (rec.Status === "LOP") { color = "#dc2626"; break; }
      if (rec.Status === "HOLIDAY") { color = "#f59e0b"; break; }
      if (rec.RecordType === "SUNDAY") { color = "yellow"; break; }
      if (rec.Status === "PERMISSION") { color = "lightgreen"; break; }
      if (rec.Status === "HALF DAY LEAVE") { color = "#ff9999"; break; }
      if (rec.Status === "DEFAULT" || "Default") { color = "#d1d5db"; break; }
    }
    return color;
  };

  useEffect(() => {
    const svg = d3.select(ref.current);
    svg.selectAll("*").remove();

    const width = ref.current.clientWidth;
    const isMobile = width < 768;
    const height = isMobile ? 320 : 420;
    const cellSize = isMobile ? 40 : 70;
    const radius = isMobile ? 15 : 25;

    const startOfMonth = new Date(year, month, 1);
    const endOfMonth = new Date(year, month + 1, 0);
    const startDate = d3.timeSunday.floor(startOfMonth);
    const endDate = d3.timeSaturday.ceil(endOfMonth);
    const days = d3.timeDays(startDate, d3.timeDay.offset(endDate, 1));

    const numWeeks = Math.ceil(days.length / 7);
    const totalWidth = 7 * cellSize;
    const totalHeight = numWeeks * cellSize;

    const offsetX = (width - totalWidth) / 2 + cellSize / 2;
    const offsetY = (height - totalHeight) / 2 + 30;

    const g = svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .append("g")
      .attr("transform", `translate(${offsetX}, ${offsetY})`);

    const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    g.selectAll("text-weekday")
      .data(daysOfWeek)
      .enter()
      .append("text")
      .attr("x", (d, i) => i * cellSize)
      .attr("y", -30)
      .attr("text-anchor", "middle")
      .attr("font-size", width < 500 ? "11px" : "14px")
      .attr("font-weight", "600")
      .attr("fill", "#0e497b")
      .text((d) => d);

    g.selectAll("circle-day")
      .data(days)
      .enter()
      .append("circle")
      .attr("cx", (d) => d.getDay() * cellSize)
      .attr("cy", (d, i) => Math.floor(i / 7) * cellSize)
      .attr("r", radius)
      .attr("fill", (d) => getDayColor(d3.timeFormat("%Y-%m-%d")(d)))
      .attr("stroke", "#fff")
      .style("cursor", "pointer")
      .on("click", (event, d) => {
        const dateStr = d3.timeFormat("%Y-%m-%d")(d);
        setSelectedDate(dateStr);
        setIsModalOpen(true);
        fetchOnduty(dateStr);
      })
      .on("mouseover", function () {
        d3.select(this).transition().duration(120).attr("r", radius + 4);
      })
      .on("mouseout", function () {
        d3.select(this).transition().duration(120).attr("r", radius);
      });

    g.selectAll("text-day")
      .data(days)
      .enter()
      .append("text")
      .attr("x", (d) => d.getDay() * cellSize)
      .attr("y", (d, i) => Math.floor(i / 7) * cellSize + 5)
      .attr("text-anchor", "middle")
      .attr("font-size", width < 500 ? "10px" : "12px")
      .attr("font-weight", "bold")
      .attr("fill", (d) => (d.getMonth() === month ? "#fff" : "#9ca3af"))
      .text((d) => d.getDate());
  }, [month, year, overAllMonthData]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
  ];

  const renderOnDutyRequests = () => {
    if (!showOnDuty || !onDutyRequests || onDutyRequests.length === 0) {
      return null;
    }

    return (
      <div style={{ marginTop: 30, padding: isMobile ? "0 10px" : "0 40px" }}>
        <h3
          style={{
            fontSize: isMobile ? 18 : 20,
            fontWeight: 700,
            marginBottom: 20,
            color: "#0e497b",
            display: "flex",
            alignItems: "center",
            gap: 10,
          }}
        >
          <FileTextOutlined />
          On-Duty Requests
        </h3>

        {onDutyRequests.map((request) => (
          <Card
            key={request.id}
            style={{
              marginBottom: 20,
              borderRadius: 12,
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              border: `2px solid ${getStatusColor(request.status)}`,
            }}
            bodyStyle={{ padding: isMobile ? "16px" : "20px" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                marginBottom: 16,
                flexWrap: "wrap",
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: isMobile ? 16 : 18, fontWeight: 700, color: "#333", marginBottom: 8 }}>
                  {request.employee_name} ({request.employee_code})
                </div>
                <div style={{ marginBottom: 6 }}>
                  <p
                    style={{
                      fontSize: isMobile ? 12 : 13,
                      color:
                        request.status === "APPROVED" ? "success"
                          : request.status === "PENDING" ? "warning"
                            : "error",
                    }}
                  >
                    {request.status}
                  </p>
                </div>
                <p><strong>Onduty Type:</strong> {request.onduty_type.toUpperCase()}</p>
              </div>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                gap: 12,
                marginBottom: 16,
                backgroundColor: "#ffffff",
                padding: isMobile ? "12px" : "16px",
                borderRadius: 8,
                border: "1px solid #ffffff",
              }}
            >
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />Start Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.start_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <ClockCircleFilled style={{ marginRight: 6 }} />End Time
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {dayjs(request.end_time).format("MMM DD, YYYY hh:mm A")}
                </div>
              </div>
              <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                  <EnvironmentOutlined style={{ marginRight: 6 }} />Location
                </div>
                <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 600, color: "#111827" }}>
                  {request.location}
                </div>
              </div>
              {request.remarks && (
                <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
                  <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>Remarks</div>
                  <div style={{ fontSize: isMobile ? 13 : 14, color: "#374151", fontStyle: "italic" }}>
                    "{request.remarks}"
                  </div>
                </div>
              )}
            </div>

            <Collapse bordered={false} style={{ backgroundColor: "#fff", borderRadius: 8 }}>
              <Panel
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600, fontSize: isMobile ? 13 : 14 }}>
                    Approval Logs ({request.approval_logs?.filter((log) => log.status === "APPROVED").length || 0}/{request.approval_logs?.length || 0})
                  </div>
                }
                key="1"
              >
                <Timeline
                  style={{ marginTop: 16 }}
                  items={request.approval_logs?.map((log) => ({
                    color: getStatusColor(log.status),
                    children: (
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                          <div>
                            <div style={{ fontWeight: 700, fontSize: isMobile ? 14 : 15, color: "#333" }}>{log.approver_name}</div>
                            <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                              Updated: {dayjs(log.updated_at).format("MMM DD, YYYY hh:mm A")}
                            </div>
                          </div>
                          <Tag color={log.status === "APPROVED" ? "success" : log.status === "PENDING" ? "warning" : "error"} style={{ fontSize: 12 }}>
                            {log.status}
                          </Tag>
                        </div>
                        <div style={{ backgroundColor: "#f9fafb", padding: "8px 12px", borderRadius: 6, fontSize: 13, color: "#555", fontStyle: "italic", marginTop: 8 }}>
                          <strong>Remarks: </strong>{log.remarks ? log.remarks : "No remarks found"}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </Panel>
            </Collapse>
          </Card>
        ))}
      </div>
    );
  };

  const goPrev = () => {
    if (month === 0) { setMonth(11); setYear((prev) => prev - 1); }
    else setMonth((prev) => prev - 1);
  };

  const goNext = () => {
    if (month === 11) { setMonth(0); setYear((prev) => prev + 1); }
    else setMonth((prev) => prev + 1);
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 10, marginTop: 10 }}>
        <Button
          onClick={onBack}
          icon={<CalendarOutlined />}
          style={{ backgroundColor: "#0e497b", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600 }}
        >
          Show Today's Attendance
        </Button>
      </div>

      <div
        style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          width: "100%", minHeight: "90vh", background: "#f8fafc", padding: "20px 10px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 20 }}>
          <Button icon={<LeftOutlined />} onClick={goPrev} />
          <strong style={{ fontSize: "clamp(16px, 2vw, 20px)", margin: "0 10px", color: "#0e497b" }}>
            {monthNames[month]} {year}
          </strong>
          <Button icon={<RightOutlined />} onClick={goNext} />
        </div>

        <svg ref={ref} style={{ width: "95%", maxWidth: 700, height: 420 }}></svg>
      </div>

      {/* --- MODAL --- */}
      <Modal
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={window.innerWidth < 700 ? "95%" : 900}
        centered
        title={
          <div style={{ textAlign: "center", fontWeight: 700, fontSize: "clamp(16px, 2vw, 18px)" }}>
            {selectedDate ? dayjs(selectedDate).format("DD/MM/YYYY") : ""}
          </div>
        }
      >
        {validFilteredData.length === 0 ? (
          <p style={{ textAlign: "center", fontSize: "clamp(14px, 2vw, 16px)", color: "#888", padding: "40px 20px" }}>
            No attendance record found for this day.
          </p>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "flex-start", width: "100%", marginTop: 10, padding: "0 10px" }}>
            <Steps direction="vertical" current={-1} style={{ width: "100%" }}>
              {validFilteredData.map((item, index) => {
                const punchDetails = item?.punch_details || [];

                return (
                  <Step
                    key={index}
                    title={
                      <div style={{ fontSize: 15, fontWeight: 700, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span
                          style={{
                            color: item.Status === "PRESENT" ? "#16a34a" : item.Status === "ABSENT" ? "#dc2626" : "#d97706",
                            fontSize: isMobile ? 15 : 16,
                          }}
                        >
                          {/* Show work sites from all punch_details entries */}

                        </span>
                      </div>
                    }
                    description={
                      <div style={{ marginTop: 10, fontSize: 14, lineHeight: 2, wordBreak: "break-word" }}>
                        {/* Map over all punch_details */}
                        {punchDetails.map((punch, punchIdx) => (
                          <div key={punchIdx} style={{ marginBottom: punchIdx < punchDetails.length - 1 ? 12 : 0 }}>
                            {
                              punch?.RecordType !== "BIOMETRIC" && (
                                <span
                                  style={{
                                    color: item.Status === "PRESENT" ? "#16a34a" : item.Status === "ABSENT" ? "#dc2626" : "#d97706",
                                    fontSize: isMobile ? 15 : 16,
                                  }}
                                >
                                  {
                                    punch.check_in_work_site && `${punch.check_in_work_site} (IN)`
                                  }
                                  {
                                    punch.check_out_work_site && `${punch.check_out_work_site} (OUT)`
                                  }
                                </span>
                              )
                            }
                            {punch.PunchIn || punch.PunchOut ? (
                              <>
                                <div><strong>Punch In:</strong> {punch.PunchIn || "No Data"}</div>
                                <div><strong>Punch Out:</strong> {punch.PunchOut || "No Data"}</div>
                              </>
                            ) : null}

                            {punch.CheckIn || punch.CheckOut ? (
                              <>
                                <div className="d-flex">
                                  <div><strong>Check In:</strong> {punch.CheckIn || "No Data"}</div>
                                  {punch.CheckIn && (
                                    <div className="ms-2">
                                      <img
                                        src={punch.check_in_photo_url || SampleImg}
                                        alt="Check-in"
                                        onClick={() => window.open(punch.check_in_photo_url || SampleImg, "_blank")}
                                        style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", cursor: "pointer" }}
                                      />
                                    </div>
                                  )}
                                </div>

                                <div className="d-flex">
                                  <div><strong>Check Out:</strong> {punch.CheckOut || "No Data"}</div>
                                  {punch.CheckOut && (
                                    <div className="ms-2">
                                      <img
                                        src={punch.check_out_photo_url || SampleImg}
                                        alt="Check-out"
                                        onClick={() => window.open(punch.check_out_photo_url || SampleImg, "_blank")}
                                        style={{ width: 50, height: 50, borderRadius: "50%", objectFit: "cover", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", cursor: "pointer" }}
                                      />
                                    </div>
                                  )}
                                </div>
                              </>
                            ) : null}

                            {punchIdx < punchDetails.length - 1 && (
                              <hr style={{ borderColor: "#e5e7eb", margin: "10px 0", borderStyle: "dashed" }} />
                            )}
                          </div>
                        ))}

                        <hr style={{ borderColor: "#eee", margin: "12px 0" }} />
                      </div>
                    }
                    icon={
                      <ClockCircleFilled
                        style={{
                          color: item.Status === "PRESENT" ? "#16a34a" : item.Status === "ABSENT" ? "#dc2626" : "#d97706",
                          fontSize: 18,
                        }}
                      />
                    }
                  />
                );
              })}
            </Steps>
          </div>
        )}

        {renderOnDutyRequests()}
      </Modal>
    </>
  );
}